import { watch, WatchHandle } from "vue";
import { AxiosError, Method } from "axios";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { checkPlayerRestrictions } from "@sazka/ng/ng-player-restrictions";
import { useNgSession } from "@sazka/ng/ng-session";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { logEvent } from "@sazka/utils/logger/use-app-logger";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import { drawGamesHttpClient } from "@/lotteries/services/my-tickets/http-client";
import { useIdentityVerificationPopup } from "@/shared/components/identity-verification/use-identity-verification-popup";
import { MIN_DEPOSIT_AMOUNT } from "@/shared/utils/constants";
import { AnalyticsHelper } from "../analytics/analytics-helper";
import { useSubscription } from "../composables/use-subscription";
import { sazkaAlertOnline } from "../online-errors";
import { useLotteryCheckoutManager } from "./lottery-checkout-manager";
import { useLotteryCheckoutStore } from "./lottery-checkout-store";

const { refreshSession, isLoggedIn, refreshBalance, hasEnoughMoney, getMissingMoney } = useNgSession();

export interface LotteryCheckoutConfig {
    isSubscriptionInternal: () => boolean;
    key: string;
    betServiceUrl: string;
    httpMethod: Method;
    useDefaultAnalytics: boolean;
    isRestarted: boolean;

    getCost?(): number;

    getData?(): any;

    restartFlow?(postData: any, dataForRestore: any): void;

    dataForRestore?(): any;
}

export class LotteryCheckout {
    private callbacksRegistered = false;
    private resolve: (value: any) => any = null;
    private reject: (value: any) => any = null;
    private isSubscription: () => boolean = null;
    private unwatchLogin: WatchHandle = null;

    private static isCheckoutInProgress = false;

    public readonly key: string = null;
    public postData: any = null;
    public useAnalytics = true;

    public getCost: () => number;
    private getDataInternal: () => any;
    private restartFlowInner: (postData: any, dataForRestore: any) => void;
    public dataForRestore: () => any;

    public url: string = null;
    public httpMethod: Method = "POST";
    public storedData = null;
    public isRestarted = false;

    private store: ReturnType<typeof useLotteryCheckoutStore>;

    constructor(config: Partial<LotteryCheckoutConfig>) {
        this.store = useLotteryCheckoutStore();
        this.key = config.key;
        if (config) this.setConfig(config);
        useLotteryCheckoutManager().registerCheckout(this);
    }

    public start(config?: Partial<LotteryCheckoutConfig>) {
        if (config) this.setConfig(config);
        this.store.checkoutKey = this.key;

        if (!this.isRestarted && this.isSubscription && this.store.hasSubscriptionIds) {
            this.store.subscriptionIds = null;
        }

        // TODO better type for return type
        return new Promise<Record<string, any>>((res, rej) => {
            this.store.betState = "waiting-for-system";

            this.resolve = res;
            this.reject = rej;

            this.registerCallbacks();
            this.betFlow();
        });
    }

    public continue() {
        this.betFlow();
    }

    public cancel() {
        this.resolve = null;
        this.reject = null;
        this.store.$reset();
        this.storedData = null;
        this.unwatchLogin();
    }

    public restartFlow(storedData: any, dataForRestore: any) {
        this.storedData = storedData;
        if (this.restartFlowInner) {
            this.restartFlowInner(storedData, dataForRestore);
        } else {
            useLotteryCheckoutManager().showBetNotPlacedWarning();
            this.cancel();
        }
    }

    public setConfig(config: Partial<LotteryCheckoutConfig>) {
        if (config.useDefaultAnalytics != null) this.useAnalytics = config.useDefaultAnalytics;
        if (config.betServiceUrl != null) this.url = config.betServiceUrl;
        if (config.httpMethod != null) this.httpMethod = config.httpMethod;
        this.isRestarted = config.isRestarted;
        if (config.isSubscriptionInternal != null) this.isSubscription = config.isSubscriptionInternal;
        if (config.getCost != null) this.getCost = config.getCost;
        if (config.getData != null) this.getDataInternal = config.getData;
        if (config.dataForRestore != null) this.dataForRestore = config.dataForRestore;
        if (config.restartFlow != null) this.restartFlowInner = config.restartFlow;
    }

    public getData() {
        return this.getDataInternal ? this.getDataInternal() : null;
    }

    private async betFlow(): Promise<void> {
        Loader.showAboveAll();
        // this try block is just for the loader to be hidden in all cases
        try {
            // check bet limit
            if (this.isAboveCostLimitValidation(this.getCost())) {
                this.cancel();
                sazkaAlert("Tuto sázku bohužel nelze vsadit", "Překročili jste limit maximální sázky 500 000 Kč.");
                return;
            }

            // wait for NG framework load
            await ngFrameworkLoaded();

            // check valid session
            refreshSession();
            if (!isLoggedIn.value) {
                this.store.betState = "waiting-for-login";
                useNgSession().showRegistrationOrLogin();
                return;
            }

            // check player gaming restrictions
            const isRestricted = await checkPlayerRestrictions(false, false, true);
            if (isRestricted) {
                this.cancel();
                return;
            }

            // check balance
            await refreshBalance(true);
            if (this.isSubscription?.()) {
                // dont have subscription ID previous page load
                const hadSubscriptionIds = this.store.hasSubscriptionIds;
                if (!this.store.hasSubscriptionIds) {
                    try {
                        this.store.subscriptionIds = await useSubscription().getSubscriptionIds();
                    } catch {
                        sazkaAlert(
                            "Nastal problém při vsazení sázky",
                            "Nepodařilo se nám zažádat o vytvoření trvalého příkazu. Vaše sázka není vsazená. Zkuste to prosím znovu."
                        );
                        this.cancel();
                    }
                }

                if (!hadSubscriptionIds || !hasEnoughMoney(this.getCost())) {
                    this.store.betState = "waiting-for-deposit";

                    ng_fw.showDeposit({
                        amount: this.getCost(),
                        mode: "externalsubscription",
                        subscriptionOptions: {
                            subscriptionId: this.store.subscriptionIds.subscriptionId,
                            isRecurringPayment: true,
                        },
                    });

                    logEvent("subscription", { message: "ng_fw.showDeposit called" });

                    return;
                }
            } else if (!hasEnoughMoney(this.getCost())) {
                if (this.store.betState === "deposit-pending") {
                    this.cancel();
                    this.showPendingAlert();
                    return;
                }
                this.store.betState = "waiting-for-deposit";
                // deposit amount is at least 10 CZK, NG cannot deposit less
                const depositAmount = Math.max(getMissingMoney(this.getCost()) / 100, MIN_DEPOSIT_AMOUNT);
                ng_fw.showDeposit("externalcheckout", depositAmount);
                return;
            }

            if (LotteryCheckout.isCheckoutInProgress) {
                this.cancel();
                return;
            }

            // api request logic
            try {
                LotteryCheckout.isCheckoutInProgress = true;

                const postData = this.storedData || this.getData();

                // add subscription ids to data payload
                if (this.store.hasSubscriptionIds) {
                    // both cassings added
                    postData.subscriptionIds = this.store.subscriptionIds;
                    postData.SubscriptionIds = this.store.subscriptionIds;
                }

                const response = (
                    await drawGamesHttpClient.request({
                        url: this.url,
                        method: this.httpMethod,
                        data: postData,
                    })
                ).data;

                try {
                    refreshBalance(true);
                    eventBus.emit(GlobalEvents.LoyaltyBet);
                    this.resolve({ subscriptionId: this.store.subscriptionIds?.subscriptionId, ...response });
                } catch (e: any) {
                    this.cancel();
                    console.error("Error after successful bet:", e);
                    sazkaAlert(
                        "Nastal problém po vsazení sázky",
                        "Vaše sázka je pravděpodobně vsazená, ale došlo k problému při zpracování odpovědi. Před další akci si prosím zkontrolujte svoje aktivní sázky."
                    );
                }
            } catch (e: unknown) {
                const error = e as AxiosError;
                this.onResponseError(error.response?.data);
                this.reject(e);
            } finally {
                this.cancel();
                LotteryCheckout.isCheckoutInProgress = false;
            }
        } finally {
            Loader.hide();
        }
    }

    private registerCallbacks() {
        if (this.callbacksRegistered) return;

        eventBus.on(GlobalEvents.WebLoginAborted, () => this.cancel());
        this.unwatchLogin = watch(isLoggedIn, (newVal) => {
            if (newVal && this.store.betState === "waiting-for-login") this.betFlow();
        });

        this.callbacksRegistered = true;
    }

    private showPendingAlert() {
        sazkaAlert(
            "SÁZKA NEBYLA PŘIJATA",
            "<br>Vklad na herní účet trval déle než obvykle a čas pro vsazení vypršel.<br>Vsaďte prosím za chvíli, až budou peníze připsané na váš herní účet.",
            null,
            null,
            "Zavřít"
        );
    }

    private async onResponseError(data) {
        if (this.useAnalytics) {
            AnalyticsHelper.pushCart();
            AnalyticsHelper.pushOrder(0);
            AnalyticsHelper.pushError(data.ErrorCode ?? data.errorCode ?? "General error occured.");
            AnalyticsHelper.measureEvent();
        }

        if ((data.ErrorCode ?? data.errorCode) === "AML_POS_ID") {
            const { showIdentityVerificationPopup } = useIdentityVerificationPopup();
            await showIdentityVerificationPopup();
            return;
        }

        sazkaAlertOnline(data.AlertType ?? data.alertType);
    }

    private isAboveCostLimitValidation(betCost: number): boolean {
        const totalPrice = Math.round(betCost);
        return totalPrice > 500_000;
    }
}
